import { Box, Button, Flex, HStack, Icon, Spinner, Text } from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FiArrowLeft, FiBarChart2, FiCalendar, FiCheckCircle, FiClock, FiList, FiPlayCircle, FiSettings } from "react-icons/fi";
import { getVotingDetails, getVotingResults, VotingResults, VOTING_STATUSES } from "@lubbezposrednio-frontend/core";
import { OrganizerPage } from "../OrganizerDashboardView/components/OrganizerShell";
import { VotingResultsContent } from "./VotingResultsContent";

const STATUS_META: Record<string, { label: string; color: string; bg: string; grad: string; icon: React.ElementType }> = {
  ongoing: { label: "Trwa", color: "#1f9e18", bg: "rgba(47,206,37,0.12)", grad: "linear(135deg, #1f9e18, #2FCE25)", icon: FiPlayCircle },
  planned: { label: "Planowane", color: "#2563eb", bg: "rgba(37,99,235,0.1)", grad: "linear(135deg, #2563eb, #4f86f7)", icon: FiClock },
  finished: { label: "Zakończone", color: "#430098", bg: "rgba(67,0,152,0.1)", grad: "linear(135deg, #430098, #7b2ff7)", icon: FiCheckCircle },
  draft: { label: "Robocze", color: "#6b7280", bg: "rgba(107,114,128,0.12)", grad: "linear(135deg, #6b7280, #9ca3af)", icon: FiList }
};

const formatDate = (iso?: string) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("pl-PL", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

type DetailVoting = { title?: string; status?: string; start_date?: string; end_date?: string };

export const ResultsView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [results, setResults] = useState<VotingResults | null>(null);
  const [voting, setVoting] = useState<DetailVoting | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    document.title = "Wyniki głosowania";
    if (!id) return;
    Promise.all([getVotingResults(id), getVotingDetails(id).catch(() => null)])
      .then(([resultsRes, detailsRes]) => {
        setResults(resultsRes.data);
        setVoting((detailsRes?.data as { voting?: DetailVoting })?.voting ?? null);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [id]);

  const statusMeta = STATUS_META[voting?.status ?? ""] ?? STATUS_META.finished;
  const isFinished = (voting?.status ?? "") === VOTING_STATUSES.FINISHED;

  return (
    <OrganizerPage>
      <Box maxW="820px" mx="auto">
        {loading ? (
          <Flex justify="center" py="90px">
            <Spinner color="primary" size="lg" thickness="3px" />
          </Flex>
        ) : error || !results ? (
          <Text color="gray.500" textAlign="center" py="60px">
            Nie udało się pobrać wyników.
          </Text>
        ) : (
          <>
            {/* ── NAGŁÓWEK (w szerokości treści) ── */}
            <Box position="relative" overflow="hidden" bg="white" borderRadius="20px" border="1px solid rgba(67,0,152,0.08)" boxShadow="0 10px 30px rgba(67,0,152,0.08)" mb="20px">
              <Box h="6px" bgGradient={statusMeta.grad} />
              <Box p={{ base: "18px", md: "22px 24px" }}>
                <HStack as={Link} to={`/organizator/glosowanie/${id}`} spacing="6px" color="gray.400" fontWeight="700" fontSize="13px" mb="13px" _hover={{ color: "primary" }} w="fit-content">
                  <Icon as={FiArrowLeft} boxSize="14px" />
                  <Text>Zarządzanie głosowaniem</Text>
                </HStack>

                <Flex gap="14px" align="flex-start" justify="space-between" direction={{ base: "column", md: "row" }}>
                  <HStack spacing="14px" align="flex-start" minW={0}>
                    <Flex w="50px" h="50px" borderRadius="14px" bgGradient={statusMeta.grad} align="center" justify="center" flexShrink={0} boxShadow={`0 8px 18px ${statusMeta.color}40`}>
                      <Icon as={FiBarChart2} boxSize="24px" color="white" />
                    </Flex>
                    <Box minW={0}>
                      <HStack spacing="8px" mb="5px" wrap="wrap">
                        <Text fontSize="11px" fontWeight="800" color="accent" textTransform="uppercase" letterSpacing="0.06em">
                          Wyniki głosowania
                        </Text>
                        <HStack spacing="5px" bg={statusMeta.bg} color={statusMeta.color} px="8px" py="2px" borderRadius="full">
                          <Icon as={statusMeta.icon} boxSize="11px" />
                          <Text fontSize="11px" fontWeight="800">{statusMeta.label}</Text>
                        </HStack>
                      </HStack>
                      <Text fontSize={{ base: "20px", md: "24px" }} fontWeight="900" color="primary" letterSpacing="-0.02em" lineHeight="1.15" noOfLines={3}>
                        {voting?.title || "Głosowanie"}
                      </Text>
                      <HStack spacing="7px" color="gray.500" fontSize="13px" mt="7px" wrap="wrap">
                        <Icon as={FiCalendar} boxSize="13px" />
                        <Text>{formatDate(voting?.start_date)} — {formatDate(voting?.end_date)}</Text>
                      </HStack>
                    </Box>
                  </HStack>
                  <Button as={Link} to={`/organizator/glosowanie/${id}`} leftIcon={<FiSettings />} size="sm" variant="outline" borderColor="rgba(67,0,152,0.22)" color="primary" borderRadius="11px" fontWeight="700" flexShrink={0} _hover={{ bg: "rgba(67,0,152,0.06)" }}>
                    Zarządzaj
                  </Button>
                </Flex>

                {!isFinished && (
                  <Flex align="center" gap="9px" bg="rgba(37,99,235,0.06)" border="1px solid rgba(37,99,235,0.18)" borderRadius="12px" p="11px 14px" mt="16px">
                    <Icon as={FiClock} color="#2563eb" boxSize="16px" flexShrink={0} />
                    <Text fontSize="13px" color="gray.600">
                      Głosowanie jeszcze trwa — wyniki są wstępne i mogą się zmienić do zakończenia.
                    </Text>
                  </Flex>
                )}
              </Box>
            </Box>

            <VotingResultsContent results={results} votingId={id} />
          </>
        )}
      </Box>
    </OrganizerPage>
  );
};
